import { ReactNode, useCallback, useRef } from 'react';

import {
  FormFieldsHandle,
  FormFieldsProvider,
} from '../../context/FormFieldsContext';
import ContentBui, { ContentBuiProps } from './ContentBui';
import SubmitErrorBui from './SubmitErrorBui';

export type FormBuiProps<T extends Record<string, unknown>> = Omit<
  ContentBuiProps,
  'component' | 'onSubmit' | 'children'
> & {
  initialValues: T;
  onSubmit: (values: T) => void | Promise<void>;
  children: ReactNode;
};

export const FormBui = <T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  children,
  ...contentProps
}: FormBuiProps<T>) => {
  const formRef = useRef<FormFieldsHandle<T>>(null);

  const handleSubmit = useCallback(async () => {
    if (!formRef.current) {
      throw new Error('Form is not ready');
    }
    await onSubmit(formRef.current.values as T);
  }, [onSubmit]);

  return (
    <ContentBui component="form" onSubmit={handleSubmit} {...contentProps}>
      <FormFieldsProvider ref={formRef} initialValues={initialValues}>
        <SubmitErrorBui />
        {children}
      </FormFieldsProvider>
    </ContentBui>
  );
};

FormBui.displayName = 'DialogCompositeBui.Form';
